import React, {useRef, useState, useEffect} from 'react';
import {Container, ContentLayout, SpaceBetween, Spinner, Box, ButtonDropdown, Button, Header} from '@amzn/awsui-components-react';
import {Notification} from '../navigation/page-layout';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import ModelExplainerTable from './model-explainer/model-explainer-table';
import ModelExplainerLineChart from './model-explainer/model-explainer-line-chart';
import ModelExplainerFilters from './model-explainer/model-explainer-filters';
import {useGetForecastIdsMetadata} from '../hooks/use-forecast-store-api';
import {translateErrorToReactNode} from '../common';
import {useGetExplainabilityAgg, useGetExplainabilityMetadata} from '../hooks/use-analytics-api';
import {ForecastIdMetaData} from '@amzn/f3-excelsior-forecast-store-lambda/clients/f3excelsiorforecaststorelambda';
import PortalLoader from '../common/PortalLoader';
import {
    ExplainabilityFilterValuesList,
    NodeDetails,
} from '@amzn/f3-excelsior-analytics-api/clients/analyticsapiforexcelsioranalytics';
import _ from 'lodash';

const DAYS_TO_INSPECT_FORECAST_METADATA = 28; // 4 weeks
const DAYS_TO_INSPECT_FORECAST = 28; // 4 weeks

/**
 * Per-model configuration for the shared explainability page.
 *
 * Insight types differ per model (e.g. BOSCO writes shap_* means while the
 * foundation model writes comp_* decomposition components), but the page
 * behavior (forecast selection, metadata-driven filters, agg query, chart and
 * table rendering) is identical. Each model provides its column sets and
 * friendly names here.
 */
export type ForecastBand = {
    /** Insight type holding the lower bound of the interval (e.g. 'forecast_q10') */
    lowerColumn: string;
    /** Insight type holding the upper bound of the interval (e.g. 'forecast_q90') */
    upperColumn: string;
    /** Legend/tooltip label for the interval */
    label: string;
};

export type ModelExplainerConfig = {
    /** model_name used in the analytics API filter (e.g. 'bosco', 'foundation') */
    modelName: string;
    /** Insight types produced by the model itself (shown with % toggle in the table) */
    modelComponentColumns: string[];
    /** Forecast-level series shown in the top chart and bolded in the table */
    forecastColumns: string[];
    /**
     * Optional prediction intervals shaded behind the forecast line, widest
     * first. Each band is rendered only when BOTH of its insight types are
     * present in the returned data, so a model run predating the quantile
     * insight types renders exactly as before.
     */
    forecastBands?: ForecastBand[];
    /**
     * Optional insight type holding recent actuals (customer demand the model
     * trained on) for the days leading up to the forecast start. Rendered as
     * dots on the forecast panel, and only when present in the returned data.
     */
    actualsColumn?: string;
    /** Post-process contribution insight types (shared PostProcessingExplainer output) */
    postProcessColumns: string[];
    /** Row order of the table (post-process rows are nested under post_process_contributions) */
    finalTableColumns: string[];
    /** Display names for all columns above */
    friendlyNameMapping: Record<string, string>;
    /** Column used as the denominator for the table's percentage toggle */
    percentageBaseColumn: string;
    /** Label of the table's percentage toggle */
    percentageToggleLabel: string;
};

type SelectedFilter = {
    nodes: NodeDetails;
    granularity: any;
    balanceDateStart: string;
    balanceDateEnd: string;
};

type FilterValues = {
    nodes: NodeDetails;
    granularities: any;
    balanceDateEarliest: string;
    balanceDateLatest: string;
};

type Records = {
    [key: string]: string | number;
};

/**
 * Given the metadata filter values for a model run, compute the default
 * granularity selection (first value of each granularity key) and the matching
 * filter value row. Used both to initialize the filters and to decide whether
 * the run has any usable explainability data.
 */
function resolveDefaultFilterSelection(explainabilityFilterValues: ExplainabilityFilterValuesList) {
    const granularitiesList = explainabilityFilterValues.map((value) => value.granularity);
    const granularitiesAgg = granularitiesList.reduce((acc, granularity) => {
        for (const key in granularity) {
            if (!acc[key]) {
                acc[key] = new Set(); // Initialize the array for the key if it doesn't exist
            }
            acc[key].add(granularity[key]); // Add the value to the corresponding array
        }
        return acc;
    }, {} as Record<string, Set<string>>);

    const setGranularity = Object.fromEntries(Object.entries(granularitiesAgg || {}).map(([key, set]) => [key, [...set][0]]));

    const explainabilityFilterValue = explainabilityFilterValues.find((value) => _.isEqual(value.granularity, setGranularity));

    return {granularitiesAgg, setGranularity, explainabilityFilterValue};
}

/**
 * Compute the default balance date window for a model run.
 *
 * The window always starts at the earliest date the model run has insights for
 * so nothing is hidden, and ends `DAYS_TO_INSPECT_FORECAST` after the forecast
 * starts. Anchoring the end on the forecast start (rather than on the earliest
 * date) matters for models that also publish pre-forecast insights such as
 * recent actuals: without it the default window would land entirely in the
 * actuals period and show no forecast at all.
 *
 * When the run has no pre-forecast insights the earliest date IS the forecast
 * start, so this returns exactly the same window as anchoring on the earliest
 * date — i.e. unchanged behaviour for models that only write forecast rows.
 */
function resolveDefaultDateWindow(balanceDateEarliest?: string, forecastStartDate?: string) {
    if (!balanceDateEarliest) {
        return {balanceDateStart: '', balanceDateEnd: ''};
    }

    const forecastStart = forecastStartDate?.substring(0, 10);
    const anchor = forecastStart && forecastStart > balanceDateEarliest ? forecastStart : balanceDateEarliest;

    const anchorAsDate = new Date(anchor);
    anchorAsDate.setDate(anchorAsDate.getDate() + DAYS_TO_INSPECT_FORECAST);

    return {
        balanceDateStart: balanceDateEarliest,
        balanceDateEnd: anchorAsDate.toISOString().substring(0, 10),
    };
}

export function ModelExplainer(props: {config: ModelExplainerConfig; pushNotification: (notification: Notification) => void}) {
    const {config} = props;
    const auth = useAuth();
    const initialized = useRef<boolean>(false);
    const isLoadingFilters = useRef<boolean>(true);
    const isLoadingData = useRef<boolean>(true);
    const isLoadingForecasts = useRef<boolean>(true);
    const isLoadingDataError = useRef<boolean>(false);

    const [forecastMetadata, setForecastMetadata] = useState<ForecastIdMetaData[]>([]);
    const [data, setData] = useState<Records[]>([]);
    const [explainabilityMetadata, setExplainabilityMetadata] = useState<ExplainabilityFilterValuesList>([]);
    const [filterValues, setFilterValues] = useState<FilterValues>({
        nodes: [],
        granularities: {},
        balanceDateEarliest: '',
        balanceDateLatest: '',
    });
    const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>({
        nodes: filterValues.nodes,
        granularity: {},
        balanceDateStart: '',
        balanceDateEnd: '',
    });
    const [selectedForecast, setSelectedForecast] = useState<ForecastIdMetaData>();

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
        flow: auth.authInformation!.current!.flow,
    };

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);

    const currentTime = new Date();
    const startTime = new Date(new Date(currentTime).setDate(currentTime.getDate() - DAYS_TO_INSPECT_FORECAST_METADATA));

    const forecastStoreViewClientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ForecastStoreView);

    const {
        status: getForecastIdsMetadataStatus,
        value: forecastIdsMetadata,
        execute: getForecastIdsMetadata,
    } = useGetForecastIdsMetadata(
        forecastStoreViewClientConfiguration,
        {
            businessId: auth.authInformation!.current!.businessId,
            country: auth.authInformation!.current!.country,
            flow: auth.authInformation!.current!.flow,
            startTime: startTime.valueOf().toString(),
            endTime: currentTime.valueOf().toString(),
        },
        false,
        createErrorListener('GetForecastIdsMetadata failed'),
        [auth]
    );

    const {
        execute: executeGetExplainabilityAgg,
        status: getExplainabilityAggStatus,
        value: retrieveGetExplainabilityAggResponse,
    } = useGetExplainabilityAgg(clientConfiguration, createErrorListener('GetExplainabilityAgg failed'), [auth]);

    const {
        execute: executeGetExplainabilityMetadata,
        status: getExplainabilityMetadataStatus,
        value: retrieveGetExplainabilityMetadataOutput,
    } = useGetExplainabilityMetadata(clientConfiguration, createErrorListener('GetExplainabilityMetadata failed'), [auth]);

    isLoadingForecasts.current = ['idle', 'pending'].includes(getForecastIdsMetadataStatus);
    isLoadingFilters.current = ['idle', 'pending'].includes(getExplainabilityMetadataStatus);
    isLoadingData.current = ['idle', 'pending'].includes(getExplainabilityAggStatus);
    isLoadingDataError.current = getExplainabilityAggStatus === 'error';

    // `selectedForecast` is populated by an effect, so on the render where the
    // forecast list first resolves it is still undefined while the metadata
    // effect below already runs. Fall back to the same forecast that effect
    // will select so the initial agg request is built against the right run.
    const activeForecast = selectedForecast ?? forecastIdsMetadata?.[0];

    // The selected model run has no usable explainability data when the
    // metadata call succeeded but returned no rows (explainability was never
    // processed for the run) or no row anywhere carries a balance date range.
    // In that case the agg request must NOT be fired — its required filter
    // fields would be missing — and the empty state is rendered instead.
    // Derived from the metadata response directly so it can never get out of
    // sync with effect ordering. Note this is deliberately a whole-run check:
    // a per-granularity date gap is handled by the applyDisabled guard on the
    // filters, so switching to a granularity that has dates still works.
    const metadataResult = retrieveGetExplainabilityMetadataOutput?.result;
    const noExplainabilityData =
        !isLoadingFilters.current &&
        metadataResult !== undefined &&
        (metadataResult.length === 0 || metadataResult.every((row) => !row.balanceDateEarliest));

    if (!initialized.current) {
        getForecastIdsMetadata();
        initialized.current = true;
    }

    useEffect(() => {
        if (!isLoadingForecasts.current && forecastIdsMetadata && forecastIdsMetadata.length > 0) {
            setForecastMetadata(forecastIdsMetadata);
            setSelectedForecast(forecastIdsMetadata[0]);
        }
    }, [isLoadingForecasts.current]);

    useEffect(() => {
        if (selectedForecast) {
            executeGetExplainabilityMetadata({
                ...tenant,
                filter: JSON.stringify({
                    modelRunId: selectedForecast.modelRunId,
                    modelName: config.modelName,
                }),
            });
        }
    }, [selectedForecast]);

    useEffect(() => {
        if (!isLoadingFilters.current && retrieveGetExplainabilityMetadataOutput?.result) {
            const explainabilityFilterValues = retrieveGetExplainabilityMetadataOutput.result;

            // No metadata rows for this model run: explainability data was
            // never produced. Skip the filter setup and the agg request
            // entirely (noExplainabilityData renders the empty state).
            // Reset the filter state explicitly so the always-rendered
            // filters don't keep showing the previous forecast's values
            // (granularity options in particular are not cleared by the
            // metadata-keyed effect below).
            if (explainabilityFilterValues.length === 0) {
                setExplainabilityMetadata([]);
                setData([]);
                setFilterValues({
                    nodes: [],
                    granularities: {},
                    balanceDateEarliest: '',
                    balanceDateLatest: '',
                });
                setSelectedFilter({
                    nodes: [],
                    granularity: {},
                    balanceDateStart: '',
                    balanceDateEnd: '',
                });
                return;
            }

            setExplainabilityMetadata(explainabilityFilterValues);
            const {granularitiesAgg, setGranularity, explainabilityFilterValue} =
                resolveDefaultFilterSelection(explainabilityFilterValues);

            setFilterValues({
                nodes: explainabilityFilterValue?.nodes || [],
                granularities: granularitiesAgg || {},
                balanceDateEarliest: explainabilityFilterValue?.balanceDateEarliest || '',
                balanceDateLatest: explainabilityFilterValue?.balanceDateLatest || '',
            });

            const selectedNodes = explainabilityFilterValue?.nodes || [];

            const {balanceDateStart, balanceDateEnd} = resolveDefaultDateWindow(
                explainabilityFilterValue?.balanceDateEarliest,
                activeForecast?.forecastStartDate
            );

            setSelectedFilter({
                nodes: selectedNodes,
                granularity: setGranularity,
                balanceDateStart: balanceDateStart || '',
                balanceDateEnd: balanceDateEnd,
            });

            // Metadata rows exist but carry no usable date range — skip the
            // agg request (noExplainabilityData renders the empty state
            // rather than sending a filter missing balanceDateStart).
            if (!balanceDateStart) {
                setData([]);
                return;
            }

            executeGetExplainabilityAgg({
                ...tenant,
                filter: JSON.stringify({
                    modelRunId: activeForecast?.modelRunId,
                    modelName: config.modelName,
                    granularity: setGranularity,
                    balanceDateStart: balanceDateStart,
                    balanceDateEnd: balanceDateEnd,
                }),
            });
        }
    }, [isLoadingFilters.current]);

    useEffect(() => {
        const explainabilityFilterValue = explainabilityMetadata.find((value) =>
            _.isEqual(value.granularity, selectedFilter.granularity)
        );

        setFilterValues((prev) => ({
            ...prev,
            nodes: explainabilityFilterValue?.nodes || [],
            balanceDateEarliest: explainabilityFilterValue?.balanceDateEarliest || '',
            balanceDateLatest: explainabilityFilterValue?.balanceDateLatest || '',
        }));

        const selectedNodes = explainabilityFilterValue?.nodes || [];

        const {balanceDateStart, balanceDateEnd} = resolveDefaultDateWindow(
            explainabilityFilterValue?.balanceDateEarliest,
            activeForecast?.forecastStartDate
        );

        setSelectedFilter((prev) => ({
            ...prev,
            nodes: selectedNodes,
            balanceDateStart: balanceDateStart || '',
            balanceDateEnd: balanceDateEnd,
        }));
    }, [explainabilityMetadata, selectedFilter.granularity]);

    useEffect(() => {
        if (!isLoadingData.current && retrieveGetExplainabilityAggResponse?.records) {
            const records = retrieveGetExplainabilityAggResponse.records;
            const data = records
                .map((record) => {
                    const {balanceDate, insights} = record;
                    return {
                        ...insights,
                        balanceDate: new Date(balanceDate).toISOString().substring(0, 10),
                    };
                })
                // The agg query groups by balance_date without an ORDER BY, so
                // sort explicitly: the chart uses a category axis driven by row
                // order, and a run that also carries pre-forecast insights spans
                // two date regions that must render left-to-right.
                .sort((a, b) => (a.balanceDate < b.balanceDate ? -1 : a.balanceDate > b.balanceDate ? 1 : 0));
            setData(data);
        }
    }, [isLoadingData.current]);

    useEffect(() => {
        if (isLoadingDataError.current) {
            setData([]);
        }
    }, [isLoadingDataError.current]);

    if (isLoadingForecasts.current && isLoadingFilters.current) {
        return <PortalLoader />;
    }

    function renderVisualizations() {
        if (noExplainabilityData) {
            return (
                <Container data-testid="no-explainability-data">
                    <Box textAlign="center" color="inherit">
                        <b>No explainability data</b>
                        <Box variant="p" color="inherit">
                            No explainability data is available for this forecast. Select another forecast, or check back after the
                            explainability processing has completed.
                        </Box>
                    </Box>
                </Container>
            );
        }
        // The current granularity selection has no balance date range. No agg
        // request can be fired for it (the Apply guard blocks it), so always
        // show the placeholder: on first load it prevents an indefinite
        // spinner (agg status stays 'idle'), and after a successful load of
        // another granularity it prevents showing that granularity's stale
        // chart/table data for the dateless selection.
        if (!isLoadingFilters.current && !selectedFilter.balanceDateStart) {
            return (
                <Container data-testid="no-date-range-selected">
                    <Box textAlign="center" color="inherit">
                        <b>No balance date range for this selection</b>
                        <Box variant="p" color="inherit">
                            The selected granularity has no explainability date range. Choose another granularity and click Apply.
                        </Box>
                    </Box>
                </Container>
            );
        }
        if (isLoadingData.current) {
            return (
                <Container data-testid="data-loader">
                    <Box textAlign="center" color="inherit">
                        <Spinner size="large" />
                    </Box>
                </Container>
            );
        } else {
            return (
                <SpaceBetween direction="vertical" size="xxl">
                    <ModelExplainerLineChart
                        data={data}
                        forecastColumns={config.forecastColumns}
                        contributionColumns={[...config.modelComponentColumns, ...config.postProcessColumns]}
                        friendlyNameMapping={config.friendlyNameMapping}
                        forecastBands={config.forecastBands}
                        actualsColumn={config.actualsColumn}
                    />
                    <ModelExplainerTable
                        data={data}
                        finalColumns={config.finalTableColumns}
                        postProcessColumns={config.postProcessColumns}
                        modelComponentColumns={config.modelComponentColumns}
                        friendlyNameMapping={config.friendlyNameMapping}
                        forecastColumns={config.forecastColumns}
                        percentageBaseColumn={config.percentageBaseColumn}
                        percentageToggleLabel={config.percentageToggleLabel}
                    />
                </SpaceBetween>
            );
        }
    }

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <div>
                    <SpaceBetween direction="horizontal" size="xxl">
                        <ButtonDropdown
                            data-testid="select-forecast"
                            items={forecastMetadata.map((forecast) => ({
                                text: new Date(forecast.createdTime).toLocaleString(),
                                id: forecast.forecastId,
                            }))}
                            onItemClick={async (item) => {
                                const foundIndex = forecastMetadata.findIndex((forecast) => forecast.forecastId === item.detail.id);
                                if (foundIndex !== -1) {
                                    setSelectedForecast(forecastMetadata[foundIndex]);
                                }

                                isLoadingFilters.current = true;
                            }}
                            loading={isLoadingFilters.current}
                            disabled={isLoadingFilters.current}
                        >
                            Select Forecast
                        </ButtonDropdown>
                        <Button
                            data-testid="refresh"
                            iconName="refresh"
                            variant="normal"
                            onClick={async () => {
                                await getForecastIdsMetadata();
                            }}
                        />
                    </SpaceBetween>
                </div>
                <Header
                    description={`Forecast Start Date: ${selectedForecast && selectedForecast.forecastStartDate.substring(0, 10)}`}
                >
                    Forecast Generated on{' '}
                    {selectedForecast &&
                        new Date(selectedForecast.createdTime).toLocaleString('en-us', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            timeZoneName: 'short',
                        })}
                </Header>
                <ModelExplainerFilters
                    filterValues={filterValues}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingData={isLoadingData.current}
                    isLoadingFilterData={isLoadingFilters.current}
                    applyDisabled={!selectedFilter.balanceDateStart}
                    onApply={() => {
                        // The selected granularity's filter row may carry no
                        // balance date range, leaving balanceDateStart as ''.
                        // Sending that filter would fail backend validation /
                        // the DB date cast, so never fire the agg request
                        // without a start date (the button is also disabled).
                        if (!selectedFilter.balanceDateStart) {
                            return;
                        }
                        isLoadingData.current = true;
                        executeGetExplainabilityAgg({
                            ...tenant,
                            filter: JSON.stringify({
                                modelRunId: selectedForecast?.modelRunId,
                                modelName: config.modelName,
                                granularity: selectedFilter.granularity,
                                nodes: selectedFilter.nodes.map((node: any) => node.nodeId).join('|'),
                                balanceDateStart: selectedFilter.balanceDateStart,
                                balanceDateEnd: selectedFilter.balanceDateEnd,
                            }),
                        });
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );
}
